import { useEffect, useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import Brand from "@/components/Brand";

export default function AdminLogin() {
  const { login, register, user } = useAuth();
  const navigate = useNavigate();
  const [mode, setMode] = useState("login");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const isRegister = mode === "register";

  useEffect(() => {
    if (user) navigate("/app", { replace: true });
  }, [user, navigate]);

  const onSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    const res = isRegister ? await register(name, email, password) : await login(email, password);
    setSubmitting(false);
    if (res.ok) {
      toast.success(isRegister ? `Cuenta creada. Tu ID de cuenta es ${res.user.account_code}` : "Welcome back");
      navigate("/app", { replace: true });
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-white text-slate-900" data-testid="admin-login-page">
      {/* Left brand panel */}
      <div className="hidden md:flex flex-col justify-between p-10 cc-grid-bg bg-slate-50 border-r border-slate-200">
        <Brand variant="dark" className="h-7" />
        <div>
          <div className="text-[11px] uppercase tracking-widest text-slate-500">Signage Operations</div>
          <h2 className="font-heading text-4xl font-semibold tracking-tight text-slate-900 mt-2 max-w-md">
            One canvas for every screen in your business.
          </h2>
          <p className="text-slate-600 mt-3 max-w-md leading-relaxed">
            Sign in to manage your TVs, build playlists, and push content live across every Fire Stick in the venue.
          </p>
        </div>
        <div className="text-xs text-slate-500">© CreatiCore 2026 · Built for hospitality.</div>
      </div>

      {/* Right form */}
      <div className="flex items-center justify-center p-8 bg-white">
        <form onSubmit={onSubmit} className="w-full max-w-sm cc-slide-up" data-testid="admin-login-form">
          <div className="md:hidden flex items-center mb-8">
            <Brand variant="dark" className="h-7" />
          </div>
          <h1 className="font-heading text-3xl font-semibold text-slate-900">{isRegister ? "Crear cuenta" : "Sign in"}</h1>
          <p className="text-sm text-slate-500 mt-1">
            {isRegister ? "Recibirás un ID de 6 números para conectar tus Fire Sticks." : "Use your account credentials to continue."}
          </p>

          <div className="mt-8 space-y-4">
            {isRegister && (
              <div>
                <Label className="text-xs font-medium text-slate-600" htmlFor="name">Nombre del negocio o tu nombre</Label>
                <Input id="name" value={name} onChange={(e) => setName(e.target.value)} required className="mt-1.5 h-11" data-testid="register-name-input" />
              </div>
            )}
            <div>
              <Label className="text-xs font-medium text-slate-600" htmlFor="email">
                Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="mt-1.5 h-11"
                data-testid="admin-login-email-input"
              />
            </div>
            <div>
              <Label className="text-xs font-medium text-slate-600" htmlFor="password">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={isRegister ? 8 : undefined}
                placeholder={isRegister ? "Mínimo 8 caracteres" : undefined}
                className="mt-1.5 h-11"
                data-testid="admin-login-password-input"
              />
            </div>
          </div>

          <Button
            type="submit"
            disabled={submitting}
            className="mt-6 w-full h-11 bg-slate-900 hover:bg-slate-800 text-white"
            data-testid="admin-login-submit-button"
          >
            {submitting ? "Un momento..." : isRegister ? "Crear cuenta" : "Sign in"} <ArrowRight className="h-4 w-4 ml-2" />
          </Button>

          <button
            type="button"
            onClick={() => setMode(isRegister ? "login" : "register")}
            className="mt-4 w-full text-center text-sm text-blue-700 hover:underline"
            data-testid="auth-mode-toggle"
          >
            {isRegister ? "¿Ya tienes cuenta? Inicia sesión" : "¿No tienes cuenta? Crea una gratis"}
          </button>

          <div className="flex items-center justify-between text-xs text-slate-500 mt-6">
            <Link to="/" className="hover:text-slate-900" data-testid="back-home-link">
              ← Back to home
            </Link>
            <Link to="/tv-login" className="hover:text-slate-900" data-testid="goto-tv-login">
              Fire Stick login →
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}
